import { useState } from "react";
import type { Pedido } from "../types/pedido";
import { fetchDatosReporte } from "../lib/reportes/datos";
import { generarReporteCliente } from "../lib/reportes/reporte-cliente";
import { generarBitacora } from "../lib/reportes/bitacora";
import { subirReporteCliente, subirBitacora } from "../lib/storage";
import { supabase } from "../lib/supabase";

function limpiarTelefono(tel: string): string {
  const digits = tel.replace(/\D/g, '')
  if (digits.startsWith('52') && digits.length === 12) return digits
  if (digits.length === 10) return `52${digits}`
  return digits
}

async function obtenerUrlReporteCliente(pedido: Pedido): Promise<string> {
  if (pedido.CLIENTE_LINK) return pedido.CLIENTE_LINK
  const datos = await fetchDatosReporte(pedido.id)
  return subirReporteCliente(pedido.id, pedido.numeroPedido, generarReporteCliente(datos))
}

async function obtenerUrlBitacora(pedido: Pedido): Promise<string> {
  if (pedido.BITACORA_LINK) return pedido.BITACORA_LINK
  const datos = await fetchDatosReporte(pedido.id)
  return subirBitacora(pedido.id, pedido.numeroPedido, generarBitacora(datos))
}

export function useDescargarReportes(
  pedido: Pedido,
  onBorrarInforme?: () => void,
  onGenerado?: (clienteUrl: string, bitacoraUrl: string) => void,
) {
  const [descargandoCliente, setDescargandoCliente] = useState(false)
  const [descargandoBitacora, setDescargandoBitacora] = useState(false)
  const [enviandoWhatsApp, setEnviandoWhatsApp] = useState(false)
  const [enviandoEmail, setEnviandoEmail] = useState(false)
  const [borrandoInforme, setBorrandoInforme] = useState(false)

  const descargarReporteCliente = async () => {
    setDescargandoCliente(true)
    try {
      const url = await obtenerUrlReporteCliente(pedido)
      window.open(url, '_blank')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('Error al descargar reporte cliente:', err)
      alert(`Error reporte cliente: ${msg}`)
    } finally {
      setDescargandoCliente(false)
    }
  }

  const descargarBitacora = async () => {
    setDescargandoBitacora(true)
    try {
      const url = await obtenerUrlBitacora(pedido)
      window.open(url, '_blank')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('Error al descargar bitácora:', err)
      alert(`Error bitácora: ${msg}`)
    } finally {
      setDescargandoBitacora(false)
    }
  }

  const enviarWhatsApp = async () => {
    if (!pedido.telefono) return
    setEnviandoWhatsApp(true)
    try {
      const url = await obtenerUrlReporteCliente(pedido)
      const tel = limpiarTelefono(pedido.telefono)
      const mensaje = encodeURIComponent(
        `Estimado cliente, le compartimos su Informe de Ensayos No. ${pedido.numeroPedido}:\n${url}\n\nNota: El enlace tiene una vigencia de 90 minutos a partir de este mensaje.`
      )
      window.open(`https://wa.me/${tel}?text=${mensaje}`, '_blank')
    } catch (err) {
      console.error('Error al enviar por WhatsApp:', err)
      alert('Error al generar el reporte.')
    } finally {
      setEnviandoWhatsApp(false)
    }
  }

  const borrarInforme = async () => {
    setBorrandoInforme(true)
    try {
      const { error } = await supabase
        .from('pedidos')
        .update({ cliente_link: null, bitacora_link: null })
        .eq('id', pedido.id)
      if (error) throw new Error(error.message)
      onBorrarInforme?.()
    } catch (err) {
      console.error('Error al borrar informe:', err)
      alert('Error al borrar el informe.')
    } finally {
      setBorrandoInforme(false)
    }
  }

  const borrarYGenerarInformes = async () => {
    setBorrandoInforme(true)
    try {
      // Capturar URLs viejas ANTES de limpiar el DB — el Edge Function las borra del bucket
      const urlViejaCliente = pedido.CLIENTE_LINK || undefined
      const urlViejaBitacora = pedido.BITACORA_LINK || undefined
      await supabase.from('pedidos').update({ cliente_link: null, bitacora_link: null }).eq('id', pedido.id)
      const datos = await fetchDatosReporte(pedido.id)
      const clienteUrl = await subirReporteCliente(pedido.id, pedido.numeroPedido, generarReporteCliente(datos), urlViejaCliente)
      const bitacoraUrl = await subirBitacora(pedido.id, pedido.numeroPedido, generarBitacora(datos), urlViejaBitacora)
      onGenerado?.(clienteUrl, bitacoraUrl)
    } catch (err) {
      console.error('Error al borrar y generar informes:', err)
      alert('Error al generar los informes.')
    } finally {
      setBorrandoInforme(false)
    }
  }

  const enviarEmail = async () => {
    if (!pedido.correo) return
    setEnviandoEmail(true)
    try {
      const url = await obtenerUrlReporteCliente(pedido)
      const asunto = encodeURIComponent(`Informe de Ensayos No. ${pedido.numeroPedido}`)
      const cuerpo = encodeURIComponent(
        `Estimado cliente,\n\nAdjuntamos su Informe de Ensayos No. ${pedido.numeroPedido}.\n\nPuede verlo aquí:\n${url}\n\nSaludos,\nDexterite-Lab`
      )
      window.location.href = `mailto:${pedido.correo}?subject=${asunto}&body=${cuerpo}`
    } catch (err) {
      console.error('Error al enviar por email:', err)
      alert('Error al generar el reporte.')
    } finally {
      setEnviandoEmail(false)
    }
  }

  return {
    descargandoCliente,
    descargandoBitacora,
    enviandoWhatsApp,
    enviandoEmail,
    borrandoInforme,
    descargarReporteCliente,
    descargarBitacora,
    enviarWhatsApp,
    enviarEmail,
    borrarInforme,
    borrarYGenerarInformes,
  }
}
